import { Box, Dialog, DialogActions, DialogContent, Divider } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"

import type { InvoiceReviewAction } from "../../hooks/useReviewInvoice"
import { useReviewInvoice } from "../../hooks/useReviewInvoice"
import { canApproveInvoice, isReviewBlockedBySync } from "../../utils"

import { InvoiceReviewDetailsSection } from "./InvoiceReviewDetailsSection"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { AerosTextField } from "@/components/UiKit/AerosTextField/AerosTextField"
import { EntityLockBanner, EntityLockConflictDialog, useEntityLock, useEntityLockConflictDialog } from "@/entityLock"
import { MutationLockResultStatus } from "@/entityLock/types"
import type { Invoice } from "@/graphql/codegen/graphql"
import { EntityLockEntityType, InvoiceStatus } from "@/graphql/codegen/graphql"
import { InvoiceComments } from "@/screens/Accounting/subfeatures/Invoices/components/InvoiceRowDetails/InvoiceComments"
import { InvoiceDocuments } from "@/screens/Accounting/subfeatures/Invoices/components/InvoiceRowDetails/InvoiceDocuments"

interface InvoiceReviewDialogProps {
  invoice: Invoice
  currentUserId: string | undefined
  onClose: () => void
  onSuccess: () => void
  onRefresh?: () => void
}

const REASON_REQUIRED_MESSAGE = "Reason is required"
const REASON_MAX_LENGTH = 500

const isReasonRequired = (action: InvoiceReviewAction): boolean => action !== InvoiceStatus.Approved

export const InvoiceReviewDialog: FC<InvoiceReviewDialogProps> = ({
  invoice,
  currentUserId,
  onClose,
  onSuccess,
  onRefresh,
}) => {
  const [reason, setReason] = useState("")
  const [reasonError, setReasonError] = useState<string | null>(null)
  const { reviewInvoice, isReviewing } = useReviewInvoice()

  const { holder, isReadOnly, markLost } = useEntityLock({
    entityType: EntityLockEntityType.Invoice,
    entityId: invoice.invoiceId,
    enabled: true,
  })

  const { isLockConflictOpen, lockConflictMessage, openLockConflict, closeLockConflict, handleLockConflictRefresh } =
    useEntityLockConflictDialog({ onRefresh, markLost })

  const showApprove = canApproveInvoice(invoice, currentUserId)

  const handleReasonChange = (value: string) => {
    setReason(value)
    if (reasonError) setReasonError(null)
  }

  const handleAction = async (action: InvoiceReviewAction) => {
    const trimmed = reason.trim()

    if (isReasonRequired(action) && !trimmed) {
      setReasonError(REASON_REQUIRED_MESSAGE)
      return
    }

    const result = await reviewInvoice({
      invoiceId: invoice.invoiceId,
      action,
      comment: trimmed || undefined,
    })
    if (result.status === MutationLockResultStatus.Success) {
      onSuccess()
      onClose()
      return
    }
    if (result.status === MutationLockResultStatus.LockConflict) {
      openLockConflict(result.message)
    }
  }

  const handleApprove = () => handleAction(InvoiceStatus.Approved)
  const handleDeny = () => handleAction(InvoiceStatus.NotApproved)
  const handleCancelInvoice = () => handleAction(InvoiceStatus.Cancelled)

  if (isLockConflictOpen && lockConflictMessage) {
    return (
      <EntityLockConflictDialog
        message={lockConflictMessage}
        onClose={closeLockConflict}
        onRefresh={handleLockConflictRefresh}
      />
    )
  }

  const isActionDisabled = isReviewing || isReadOnly || isReviewBlockedBySync(invoice)

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth data-testid='accounting.invoice-review-dialog'>
      <DialogHeader title='Review Invoice' onClose={onClose} />
      <DialogContent className='flex flex-col gap-8 pt-4'>
        {isReadOnly && <EntityLockBanner holder={holder} />}

        <InvoiceReviewDetailsSection invoice={invoice} />

        <Divider />

        <InvoiceComments invoiceId={invoice.invoiceId} />

        <Divider />

        {/* AK-6407: Delete must never be available from the Payable Approval Invoice modal. */}
        <InvoiceDocuments invoiceId={invoice.invoiceId} canDelete={false} />

        <AerosTextField
          label='Reason'
          value={reason}
          onChange={handleReasonChange}
          placeholder='Enter reason...'
          multiline
          minRows={3}
          error={Boolean(reasonError)}
          helperText={reasonError ?? undefined}
          disabled={isReadOnly}
          slotProps={{ htmlInput: { maxLength: REASON_MAX_LENGTH } }}
          data-testid='accounting.invoice-review-dialog.reason-field'
        />
      </DialogContent>
      <DialogActions className='justify-between px-6 pb-6'>
        <Button
          appearance='outlined'
          onClick={onClose}
          disabled={isReviewing}
          data-testid='accounting.invoice-review-dialog.go-back-button'
        >
          Go Back
        </Button>
        <Box className='flex gap-2'>
          <Button
            appearance='outlined'
            onClick={handleCancelInvoice}
            disabled={isActionDisabled}
            data-testid='accounting.invoice-review-dialog.cancel-invoice-button'
          >
            Cancel Invoice
          </Button>
          <Button
            appearance='contained'
            variant='error'
            onClick={handleDeny}
            disabled={isActionDisabled}
            data-testid='accounting.invoice-review-dialog.deny-button'
          >
            Deny
          </Button>
          {showApprove && (
            <Button
              appearance='contained'
              onClick={handleApprove}
              disabled={isActionDisabled}
              data-testid='accounting.invoice-review-dialog.approve-button'
            >
              Approve
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}
